import { Stack } from '@ltvco/refresh-lib/theme';
import { CHATAI_LOADING_STATUS, Message } from '@ltvco/refresh-lib/v2';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import {
  MessagesContentStack,
  GenericReportResponse,
} from '@ltvco/refresh-lib/v2';
import { useState, useRef, useEffect } from 'react';
import { DamageAnalyzerContent } from './DamageAnalyzerContent';
import { MessagePair } from '../components/MessagePair';
import { RetrySearchInstruction } from '../components/RetrySearchInstruction';
import { useMessagePairing } from './hooks/useMessagePairing';
import { useScrollManagement } from '../hooks/useScrollManagement';
import { useStickyHeader } from '../hooks/useStickyHeader';
import { useTailGap } from '../hooks/useTailGap';

export interface LoadingStatus {
  message: string;
  subtitle: string;
}

type MessagesContainerProps = {
  messages: Message[];
  loadingStatus: LoadingStatus;
  onSendMessage?: (message?: string, imageBase64?: string) => void;
};

export interface MessageReportData {
  reportData: GenericReportResponse;
  type: string;
  permalink: string;
}

const MAX_TAIL_GAP = 80;

export const MessagesContainer = ({
  messages,
  loadingStatus,
  onSendMessage,
}: MessagesContainerProps) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { isMobile } = useScreenSize();
  const [lastAutoOpenedMessageContent, setLastAutoOpenedMessageContent] =
    useState<string | null>(null);
  const [imageToAnalyze, setImageToAnalyze] = useState<string | null>(null);
  const [hasImageError, setHasImageError] = useState(false);

  // Refs for layout/scroll
  const scrollRootRef = useRef<HTMLDivElement | null>(null);
  const messageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const gapRef = useRef<HTMLDivElement | null>(null);
  const typingRef = useRef<HTMLDivElement | null>(null);
  const [hasUserScrolled, setHasUserScrolled] = useState(false);

  const handleClearImage = () => {
    setImageToAnalyze(null);
    setHasImageError(false);
  };

  const handleSetImageToAnalyze = (image: string) => {
    setImageToAnalyze(image);
  };

  // Reset local image/error state when the chat conversation is cleared
  useEffect(() => {
    if (messages.length === 0) {
      setImageToAnalyze(null);
      setHasImageError(false);
    }
  }, [messages.length]);

  // Check for image analysis error in assistant messages
  // Error occurs when photoDamageEstimate contains an error field
  useEffect(() => {
    if (messages.length > 0) {
      const lastAssistantMessage = messages.findLast(
        (msg) => msg.role === 'assistant',
      );

      if (lastAssistantMessage?.photoDamageEstimate?.error !== undefined) {
        setHasImageError(true);
      }
    }
  }, [messages]);
  // Note: auto-scroll + tail gap + sticky header are handled by shared hooks.

  useEffect(() => {
    if (messages.length > 0 && !isMobile) {
      const lastMessage = messages[messages.length - 1];
      // Only auto-open if last message is an assistant report and it's a new one
      if (
        lastMessage.role === 'assistant' &&
        lastMessage.reports?.[0]?.permalink &&
        lastMessage.content !== lastAutoOpenedMessageContent
      ) {
        // Small delay to ensure the message is fully rendered
        const timer = setTimeout(() => {
          setLastAutoOpenedMessageContent(lastMessage.content || null);
        }, 100);

        return () => clearTimeout(timer);
      }
    }
  }, [messages, lastAutoOpenedMessageContent]);

  const { pairs, firstPhotoDamageAssistantIndex, firstPhotoDamageEstimate } =
    useMessagePairing(messages);

  // Shared scroll + sticky + tail gap hooks (same behavior as other ChatAI features).
  const scrollMgmt = useScrollManagement({
    scrollRootRef,
    messageRefs,
    pairs,
    messagesLength: messages.length,
    loadingMessage: loadingStatus.message,
    featureTitle: 'Photo Damage Analyzer',
  });
  useEffect(() => {
    setHasUserScrolled(scrollMgmt.hasUserScrolled);
  }, [scrollMgmt.hasUserScrolled]);

  const stickyIndex = useStickyHeader({
    scrollRootRef,
    messageRefs,
    pairs,
    messages,
  });

  const tailGap = useTailGap({
    scrollRootRef,
    messageRefs,
    gapRef,
    typingRef,
    pairs,
    loadingMessage: loadingStatus.message,
    hasError: hasImageError,
    maxTailGap: hasImageError ? 0 : MAX_TAIL_GAP,
    featureTitle: 'Photo Damage Analyzer',
  });
  const shouldShowRetrySearch =
    loadingStatus.message === CHATAI_LOADING_STATUS.IDLE.message &&
    hasImageError;

  // (scroll/sticky/tail-gap logic removed in favor of shared hooks)

  // Cleanup timeout on component unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <MessagesContentStack
      ref={scrollRootRef}
      width="100%"
      spacing={2}
      paddingBottom={isMobile ? '130px' : '20px'}
      height={isMobile ? '100%' : 'auto'}
      minHeight={isMobile ? 0 : 'auto'}
      sx={{
        flexGrow: 1,
        overflowY: 'auto',
      }}
    >
      <DamageAnalyzerContent
        onSendMessage={onSendMessage}
        hasConversationStarted={pairs.length > 0}
        firstPhotoDamageEstimate={firstPhotoDamageEstimate}
        imageToAnalyze={imageToAnalyze}
        setImageToAnalyze={handleSetImageToAnalyze}
        onImageErrorChange={setHasImageError}
        hasImageError={hasImageError}
        onClearImage={handleClearImage}
      />
      {pairs.map((pair, index) => (
        <MessagePair
          key={pair.id}
          userMessage={pair.user}
          assistantMessage={pair.assistant}
          userIndex={pair.userIndex}
          assistantIndex={pair.assistantIndex}
          isLastPair={index === pairs.length - 1}
          isSticky={stickyIndex === pair.userIndex}
          hasUserScrolled={hasUserScrolled}
          loadingStatus={loadingStatus}
          firstEstimateAssistantIndex={firstPhotoDamageAssistantIndex}
          tailGap={tailGap}
          messageRefs={messageRefs}
          typingRef={typingRef}
          gapRef={gapRef}
        />
      ))}
      {shouldShowRetrySearch && (
        <Stack width="100%" alignItems="center">
          <RetrySearchInstruction onRetrySearch={handleClearImage} />
        </Stack>
      )}
    </MessagesContentStack>
  );
};
